import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Chip,
  InputAdornment,
  Collapse,
  Divider,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Public as GlobalIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { createElement } from 'react';
import { componentsService } from '../../services/components.service';
import {
  getComponentIcon,
  getComponentIconColor,
} from '../../config/component-icons';
import type { Component } from '@risk-management/shared';

interface ComponentSelectorProps {
  spaceId: string;
  onSelectComponent: (component: Component) => void;
  onCreateComponent: () => void;
}

// Group components by type for organized display
const groupComponentsByType = (components: Component[]) => {
  const groups: Record<string, Component[]> = {};

  components.forEach(component => {
    const type = component.type || 'other';
    if (!groups[type]) {
      groups[type] = [];
    }
    groups[type].push(component);
  });

  return groups;
};

export const ComponentSelector: React.FC<ComponentSelectorProps> = ({
  spaceId,
  onSelectComponent,
  onCreateComponent: _onCreateComponent,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );

  // Fetch components for the current space
  const {
    data: components = [],
    isLoading,
    refetch,
  } = useQuery<Component[]>({
    queryKey: ['components', spaceId],
    queryFn: () => componentsService.getBySpace(spaceId, true),
    enabled: !!spaceId,
  });

  // Filter components by search query
  const filteredComponents = useMemo(() => {
    if (!searchQuery.trim()) return components;

    const query = searchQuery.toLowerCase();
    return components.filter(
      c =>
        c.name.toLowerCase().includes(query) ||
        c.type.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
    );
  }, [components, searchQuery]);

  // Group filtered components by type
  const groupedComponents = useMemo(
    () => groupComponentsByType(filteredComponents),
    [filteredComponents]
  );

  // Get sorted type names
  const sortedTypes = useMemo(
    () => Object.keys(groupedComponents).sort(),
    [groupedComponents]
  );

  const toggleGroup = (type: string) => {
    setExpandedGroups(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  // Expand all groups by default
  const expandAll = React.useCallback(() => {
    const allExpanded: Record<string, boolean> = {};
    sortedTypes.forEach(type => {
      allExpanded[type] = true;
    });
    setExpandedGroups(allExpanded);
  }, [sortedTypes]);

  // Auto-expand when searching
  React.useEffect(() => {
    if (searchQuery.trim()) {
      expandAll();
    }
  }, [searchQuery, expandAll]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Search Header */}
      <Box sx={{ p: 2, pb: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <Typography variant="subtitle2" color="text.secondary">
            {t('diagram.availableComponents')} ({components.length})
          </Typography>
          <Tooltip title={t('common.refresh')}>
            <IconButton size="small" onClick={() => refetch()}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          size="small"
          placeholder={t('diagram.searchComponents')}
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Divider />

      {/* Component List */}
      <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
        {filteredComponents.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery
                ? t('diagram.noComponentsFound')
                : t('diagram.noComponentsAvailable')}
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {sortedTypes.map(type => {
              const typeComponents = groupedComponents[type];
              const isExpanded = expandedGroups[type] ?? true; // Default to expanded
              const IconComponent = getComponentIcon(type);
              const iconColor = getComponentIconColor(type);

              return (
                <Box key={type}>
                  {/* Type Group Header */}
                  <ListItemButton
                    onClick={() => toggleGroup(type)}
                    sx={{
                      backgroundColor: 'action.hover',
                      '&:hover': { backgroundColor: 'action.selected' },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {createElement(IconComponent, {
                        size: 18,
                        color: iconColor,
                      })}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {t(
                              `componentTypes:${type}`,
                              type.charAt(0).toUpperCase() +
                                type.slice(1).replace(/_/g, ' ')
                            )}
                          </Typography>
                          <Chip
                            label={typeComponents.length}
                            size="small"
                            sx={{ height: 18, fontSize: '0.7rem' }}
                          />
                        </Box>
                      }
                    />
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </ListItemButton>

                  {/* Components in Group */}
                  <Collapse in={isExpanded} timeout="auto">
                    <List dense disablePadding>
                      {typeComponents.map(component => (
                        <ListItemButton
                          key={component.id}
                          onClick={() => onSelectComponent(component)}
                          sx={{ pl: 4 }}
                        >
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            {component.isGlobal && (
                              <Tooltip title={t('component.global')}>
                                <GlobalIcon fontSize="small" color="info" />
                              </Tooltip>
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="body2" noWrap>
                                {component.name}
                              </Typography>
                            }
                            secondary={
                              component.description ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ display: 'block', maxWidth: 180 }}
                                >
                                  {component.description}
                                </Typography>
                              ) : null
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })}
          </List>
        )}
      </Box>

      {/* Footer with Create Button */}
      <Divider />
      <Box sx={{ p: 2 }}>
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          mb={1}
        >
          {t('diagram.clickToAddComponent')}
        </Typography>
      </Box>
    </Box>
  );
};
